import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, bookUrl } from "../lib/api";
import { formatHebrewRef } from "../lib/hebrewRef";
import { useAuth } from "../context/AuthContext";
import { useLearning } from "../context/LearningContext";
import { useBasket } from "../context/BasketContext";
import {
  Highlighter,
  Bookmark,
  StickyNote,
  ShoppingBasket,
  Trash2,
  ArrowRight,
  Filter,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const HIGHLIGHT_COLOR_MAP = {
  yellow: { bg: "bg-yellow-400/30", border: "border-yellow-400/60", he: "חשוב" },
  blue: { bg: "bg-blue-500/25", border: "border-blue-400/60", he: "שאלה" },
  green: { bg: "bg-emerald-400/30", border: "border-emerald-400/60", he: "רעיון לשיעור" },
  red: { bg: "bg-rose-500/25", border: "border-rose-500/60", he: "צריך עיון" },
  purple: { bg: "bg-purple-500/25", border: "border-purple-400/60", he: "השראה" },
  gold: { bg: "bg-gold/25", border: "border-gold/60", he: "מקור מרכזי" },
  rose: { bg: "bg-rose-300/25", border: "border-rose-300/60", he: "סימון" },
  sage: { bg: "bg-emerald-300/25", border: "border-emerald-300/60", he: "סימון" },
  amber: { bg: "bg-amber-400/30", border: "border-amber-400/60", he: "סימון" },
};

// Solid swatch colors that match the .hl-{color} CSS variables used in the
// book/study mode. Used both on the filter chips and in the verse swatches
// so the user gets one consistent color language across the whole app.
const FILTER_SWATCH = {
  yellow: "#FFE082",
  blue:   "#90CAF9",
  green:  "#A5D6A7",
  red:    "#F48FB1",
  purple: "#CE93D8",
  gold:   "#F0C26A",
};

const COLOR_FILTERS = [
  { id: "all", label: "הכל" },
  { id: "yellow", label: "חשוב" },
  { id: "blue", label: "שאלה" },
  { id: "green", label: "רעיון לשיעור" },
  { id: "red", label: "צריך עיון" },
  { id: "purple", label: "השראה" },
  { id: "gold", label: "מקור מרכזי" },
];

/**
 * Beit Midrash · Highlights / Notes / Bookmarks list page.
 * Three tabs share the same shell so the user has one consistent view of all
 * the things they've collected while learning.
 */
export default function HighlightsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const initialTab = search.get("tab") === "bookmarks"
    ? "bookmarks"
    : search.get("tab") === "notes"
    ? "notes"
    : "highlights";

  const [tab, setTab] = useState(initialTab);
  const learning = useLearning();
  const { highlights, notes, bookmarks } = learning;
  const [colorFilter, setColorFilter] = useState("all");
  const [textFilter, setTextFilter] = useState("");

  const basket = useBasket();

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate("/auth?next=/beit-midrash/highlights", { replace: true });
      return;
    }
    learning.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const filteredHighlights = useMemo(() => {
    let out = highlights;
    if (colorFilter !== "all") out = out.filter((h) => h.color === colorFilter);
    if (textFilter) {
      const t = textFilter.toLowerCase();
      out = out.filter(
        (h) =>
          (h.book || "").toLowerCase().includes(t) ||
          `${h.chapter}:${h.verse}`.includes(t)
      );
    }
    return out;
  }, [highlights, colorFilter, textFilter]);

  const filteredNotes = useMemo(() => {
    if (!textFilter) return notes;
    const t = textFilter.toLowerCase();
    return notes.filter(
      (n) =>
        ((n.note_text || n.text || "")).toLowerCase().includes(t) ||
        (n.book || "").toLowerCase().includes(t)
    );
  }, [notes, textFilter]);

  const filteredBookmarks = useMemo(() => {
    if (!textFilter) return bookmarks;
    const t = textFilter.toLowerCase();
    return bookmarks.filter(
      (b) =>
        (b.book || "").toLowerCase().includes(t) ||
        `${b.chapter}:${b.verse}`.includes(t)
    );
  }, [bookmarks, textFilter]);

  if (!user) return null;

  const removeHighlight = async (id) => {
    try {
      await learning.removeHighlight(id);
      toast.success("הסימון נמחק");
    } catch {
      toast.error("שגיאה במחיקת הסימון");
    }
  };

  const removeNote = async (id) => {
    try {
      await learning.removeNote(id);
      toast.success("ההערה נמחקה");
    } catch {
      toast.error("שגיאה במחיקת ההערה");
    }
  };

  return (
    <div className="relative min-h-[calc(100vh-4rem)]" data-testid="bm-highlights-page" dir="rtl">
      <section className="mx-auto max-w-5xl px-4 sm:px-6 pt-8 pb-24">
        <div className="mb-8 flex items-center gap-3">
          <Link
            to="/beit-midrash"
            data-testid="bm-back-to-hub"
            className="text-muted-foreground hover:text-gold transition-colors"
            aria-label="חזרה לבית המדרש"
          >
            <ArrowRight className="h-5 w-5 rtl:rotate-0" strokeWidth={1.5} />
          </Link>
          <div>
            <p className="font-latin italic text-[11px] tracking-[0.35em] uppercase text-gold">
              My Beit Midrash
            </p>
            <h1 className="font-heading text-3xl text-foreground">
              הלימוד השמור שלי
            </h1>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab} dir="rtl">
          <TabsList className="bg-transparent gap-2 p-0 h-auto justify-start">
            <TabsTrigger value="highlights" data-testid="tab-highlights" className="data-[state=active]:bg-gold/15 data-[state=active]:text-gold border border-border/60 rounded-sm px-3 py-1.5">
              <Highlighter className="h-3.5 w-3.5 ms-1.5" strokeWidth={1.5} />
              סימונים ({highlights.length})
            </TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes" className="data-[state=active]:bg-gold/15 data-[state=active]:text-gold border border-border/60 rounded-sm px-3 py-1.5">
              <StickyNote className="h-3.5 w-3.5 ms-1.5" strokeWidth={1.5} />
              הערות ({notes.length})
            </TabsTrigger>
            <TabsTrigger value="bookmarks" data-testid="tab-bookmarks" className="data-[state=active]:bg-gold/15 data-[state=active]:text-gold border border-border/60 rounded-sm px-3 py-1.5">
              <Bookmark className="h-3.5 w-3.5 ms-1.5" strokeWidth={1.5} />
              סימניות ({bookmarks.length})
            </TabsTrigger>
          </TabsList>

          {/* Filter bar */}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Input
              dir="rtl"
              data-testid="bm-search-input"
              value={textFilter}
              onChange={(e) => setTextFilter(e.target.value)}
              placeholder="חיפוש לפי ספר / פרק:פסוק / טקסט..."
              className="max-w-sm bg-card border-border/60 focus-visible:ring-gold"
            />
            {tab === "highlights" && (
              <div className="flex flex-wrap items-center gap-1.5">
                <Filter className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                {COLOR_FILTERS.map((c) => {
                  const swatch = FILTER_SWATCH[c.id]; // undefined for "all"
                  const isActive = colorFilter === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setColorFilter(c.id)}
                      data-testid={`hl-color-filter-${c.id}`}
                      data-active={isActive ? "true" : "false"}
                      className={`px-2.5 py-1 rounded-sm border font-heading text-xs transition-all ${
                        isActive
                          ? "border-gold ring-1 ring-gold/40 text-foreground"
                          : "border-border/60 text-foreground hover:border-gold/60"
                      }`}
                      style={
                        swatch
                          ? {
                              backgroundColor: swatch,
                              // Darker text on every coloured chip so the
                              // Hebrew label stays readable on the pastel bg.
                              color: "#3a2c00",
                            }
                          : undefined
                      }
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="mt-6">
            <TabsContent value="highlights" className="mt-0">
              {filteredHighlights.length === 0 ? (
                <Empty
                  testid="hl-empty"
                  text="עדיין אין סימונים. סמן פסוקים חשובים תוך כדי לימוד וכאן תמצא אותם."
                />
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="hl-list">
                  {filteredHighlights.map((h) => {
                    const palette = HIGHLIGHT_COLOR_MAP[h.color] || HIGHLIGHT_COLOR_MAP.gold;
                    return (
                      <li
                        key={h.id}
                        data-testid={`hl-item-${h.id}`}
                        data-color={h.color}
                        className={`relative rounded-sm border ${palette.border} bg-card p-4 flex flex-col gap-2`}
                      >
                        <div className={`absolute inset-y-0 right-0 w-1.5 ${palette.bg}`} />
                        <div className="flex items-baseline justify-between gap-3 ps-3">
                          <Link
                            to={`/reader/${bookUrl(h.book)}/${h.chapter}?v=${h.verse}`}
                            className="font-heading text-base text-foreground hover:text-gold transition-colors"
                          >
                            {formatHebrewRef(h.book_he || h.book, h.chapter, h.verse)}
                          </Link>
                          <span className="text-[10px] font-heading tracking-widest uppercase text-muted-foreground">
                            {palette.he}
                          </span>
                        </div>
                        <div className="flex items-center justify-end gap-1 ps-3">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 px-2 text-muted-foreground hover:text-gold"
                            onClick={() => {
                              basket.add({
                                type: "verse",
                                book: h.book,
                                chapter: h.chapter,
                                verse: h.verse,
                              });
                              toast.success("נוסף לסל");
                            }}
                            data-testid={`hl-add-basket-${h.id}`}
                          >
                            <ShoppingBasket className="h-3.5 w-3.5" strokeWidth={1.5} />
                            לסל
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-xs h-8 px-2 text-muted-foreground hover:text-destructive"
                            onClick={() => removeHighlight(h.id)}
                            data-testid={`hl-remove-${h.id}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                            מחק
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="notes" className="mt-0">
              {filteredNotes.length === 0 ? (
                <Empty
                  testid="notes-empty"
                  text="עדיין אין הערות. הוסף הערה תוך כדי לימוד וכאן תמצא אותה."
                />
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="notes-list">
                  {filteredNotes.map((n) => (
                    <li
                      key={n.id}
                      data-testid={`note-item-${n.id}`}
                      className="rounded-sm border border-border/60 bg-card p-4 flex flex-col gap-2"
                    >
                      <div className="flex items-baseline justify-between gap-3">
                        <Link
                          to={`/reader/${bookUrl(n.book)}/${n.chapter}?v=${n.verse}`}
                          className="font-heading text-base text-foreground hover:text-gold transition-colors"
                        >
                          {formatHebrewRef(n.book_he || n.book, n.chapter, n.verse)}
                        </Link>
                      </div>
                      <p
                        dir="rtl"
                        className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed"
                      >
                        {n.note_text || n.text}
                      </p>
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-8 px-2 text-muted-foreground hover:text-gold"
                          onClick={() => {
                            basket.add({
                              type: "note",
                              text: `${formatHebrewRef(n.book_he || n.book, n.chapter, n.verse)} — ${n.note_text || n.text}`,
                            });
                            toast.success("נוסף לסל");
                          }}
                          data-testid={`note-add-basket-${n.id}`}
                        >
                          <ShoppingBasket className="h-3.5 w-3.5" strokeWidth={1.5} />
                          לסל
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-8 px-2 text-muted-foreground hover:text-destructive"
                          onClick={() => removeNote(n.id)}
                          data-testid={`note-remove-${n.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                          מחק
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>

            <TabsContent value="bookmarks" className="mt-0">
              {filteredBookmarks.length === 0 ? (
                <Empty
                  testid="bookmarks-empty"
                  text="עדיין אין סימניות. סמן פסוקים שתרצה לחזור אליהם."
                />
              ) : (
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="bookmarks-list">
                  {filteredBookmarks.map((b) => (
                    <li
                      key={b.id}
                      data-testid={`bookmark-item-${b.id}`}
                      className="rounded-sm border border-border/60 bg-card p-4 flex items-center justify-between gap-2"
                    >
                      <Link
                        to={`/reader/${bookUrl(b.book)}/${b.chapter}?v=${b.verse}`}
                        className="font-heading text-base text-foreground hover:text-gold transition-colors flex-1 min-w-0 truncate"
                      >
                        {formatHebrewRef(b.book_he || b.book, b.chapter, b.verse)}
                      </Link>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-xs h-8 px-2 text-muted-foreground hover:text-gold"
                          onClick={() => {
                            basket.add({
                              type: "verse",
                              book: b.book,
                              chapter: b.chapter,
                              verse: b.verse,
                            });
                            toast.success("נוסף לסל");
                          }}
                        >
                          <ShoppingBasket className="h-3.5 w-3.5" strokeWidth={1.5} />
                          לסל
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          data-testid={`bookmark-delete-${b.id}`}
                          className="text-xs h-8 px-2 text-muted-foreground hover:text-destructive"
                          onClick={async () => {
                            try {
                              await learning.toggleBookmark({
                                book: b.book,
                                chapter: b.chapter,
                                verse: b.verse,
                              });
                              toast.success("הסימנייה נמחקה");
                            } catch {
                              toast.error("שגיאה במחיקת הסימנייה");
                            }
                          }}
                          aria-label="מחק סימנייה"
                        >
                          <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />
                          מחק
                        </Button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </TabsContent>
          </div>
        </Tabs>
      </section>
    </div>
  );
}

function Empty({ text, testid }) {
  return (
    <div
      data-testid={testid}
      className="text-center text-sm text-muted-foreground border border-dashed border-border/60 rounded-sm px-4 py-10"
    >
      {text}
    </div>
  );
}
